'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Gender } from '@prisma/client';
import { IconMail } from '@tabler/icons-react';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import type { z } from 'zod';
import FormInput from '@/components/forms/form-input';
import { Form } from '@/components/ui/form';
import { visaBookingSchema } from '@/schemas';
import { api } from '@/server/callers/client';
import { trackEvent } from '@/utils/analytics';
import { usePageViewTracking } from '@/utils/hooks';

type FormData = z.infer<typeof visaBookingSchema>;

export default function VisaForm() {
  // Track page view
  usePageViewTracking();

  const form = useForm<FormData>({
    resolver: zodResolver(visaBookingSchema),
    defaultValues: {
      fullName: '',
      nationality: '',
      gender: 'Male',
      email: '',
      phone: '',
      message: '',
      destinationId: '',
      residenceCountry: '',
      dateOfArrival: new Date(),
    },
  });

  const destinations = api.destinations.list.useQuery();

  const bookMutation = api.visaProcessing.book.useMutation({
    onSuccess: async () => {
      // Track successful submission
      trackEvent({
        action: 'visa_form_submit_success',
        category: 'forms',
        label: 'Visa Form Success',
        value: 1,
      });

      toast.success('Thank you for your visa request! We will get back to you soon.');
      form.reset();
    },
    onError: (error) => {
      // Track submission error
      trackEvent({
        action: 'visa_form_submit_error',
        category: 'forms',
        label: error.message || 'Unknown error',
        value: 1,
      });

      toast.error(error.message || 'Something went wrong. Please try again.');
    },
  });

  // Track form interaction
  const hasInteracted = useRef(false);

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (!hasInteracted.current && name && value) {
        hasInteracted.current = true;

        trackEvent({
          action: 'visa_form_interaction',
          category: 'forms',
          label: 'Visa Form Interaction',
          value: 1,
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [form]);

  const handleSubmit = async (data: FormData) => {
    // Track submission attempt
    trackEvent({
      action: 'visa_form_submit',
      category: 'forms',
      label: 'Visa Form',
      value: 1,
    });

    bookMutation.mutate(data);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <FormInput
          type="text"
          id="fullName"
          label="Full Name"
          placeholder="Your full name"
          form={form}
        />

        <FormInput
          type="select"
          id="gender"
          label="Gender"
          required
          options={Object.values(Gender).map((gender) => ({
            value: gender,
            label: gender,
          }))}
          form={form}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormInput
            type="text"
            id="nationality"
            label="Nationality"
            placeholder="Your nationality"
            form={form}
          />

          <FormInput
            type="text"
            id="residenceCountry"
            label="Country of residence"
            placeholder="Your country of residence"
            form={form}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormInput
            id="email"
            label="Email"
            type="email"
            required
            placeholder="your.email@example.com"
            form={form}
          />

          <FormInput
            id="phone"
            label="Phone"
            type="tel"
            required
            placeholder="Your phone number"
            form={form}
          />
        </div>

        <FormInput
          id="destinationId"
          label="Destination"
          type="select"
          required
          options={
            destinations?.data?.map((destination: { id: string; name: string }) => ({
              value: destination.id,
              label: destination.name,
            })) ?? []
          }
          form={form}
        />

        <FormInput
          id="dateOfArrival"
          label="Date of Arrival"
          type="date"
          required
          form={form}
        />

        <FormInput
          id="message"
          label="Message"
          type="textarea"
          required
          placeholder="Additional information about your visa request"
          rows={5}
          form={form}
        />

        {bookMutation.isSuccess && (
          <div className="flex flex-col justify-center items-center gap-2 p-4 bg-green-100 rounded-lg text-green-800">
            <IconMail className="text-green-800" size={90} />
            <p className="text-lg">
              You have successfully submitted your visa request. Please check your
              email for verification. You can also check your spam folder.
            </p>
          </div>
        )}

        <button
          type="submit"
          disabled={bookMutation.isPending}
          className="bg-accent text-black font-medium rounded-lg w-fit px-12 py-3 hover:bg-accent/90 transition-colors disabled:bg-blue-400"
        >
          {bookMutation.isPending ? 'Submitting...' : 'Submit Visa Request'}
        </button>
      </form>
    </Form>
  );
}
